<script lang="ts">
  // Cube table — per-product cube display per INTERFACE-SPECIFICATION.md
  // §4.2 (read-only matrix view) and §6.2 (the editing surface, which
  // reuses this component with the `editable` prop set in commit 5).
  //
  // Renders two pricing models:
  //   - bureau_band_x_housing_x_channel: rows are bureau bands,
  //     columns are housing statuses, channel comes from the
  //     channel selector.
  //   - grade_x_subgrade_x_channel: rows are grade-subgrade pairs,
  //     columns are rate / max amount with public-band mapping at the
  //     end. Channel selector switches between hLoan and hMoney.

  import type { ProductCatalogueFile, CubeCellValue, EstablishmentFeeOverride } from '../lib/types';
  import { formatRate, formatAmount } from '../lib/formatters';
  import { housingIds as bureauHousingIds } from '../lib/cube';

  interface Props {
    catalogue: ProductCatalogueFile;
    channel: string;
    onCellClick?: (coordinates: string, before: CubeCellValue) => void;
    // Partial shape: only the fields actually staged for each coordinate.
    // An unstaged field reads as `undefined` so the cell can suppress its
    // "before → after" arrow preview when nothing changed in that field.
    stagedByCoordinate?: Record<string, Partial<CubeCellValue>>;
    // Click handler for the per-credit-rating establishment-fee override
    // sub-line under each row. `before` is null when no override is
    // present on the catalogue at that credit-rating-axis-level node.
    onOverrideClick?: (creditRatingId: string, before: EstablishmentFeeOverride | null) => void;
    // Staged override edits keyed by credit-rating id. A `null` value
    // means the user has staged the removal of an existing override; an
    // `EstablishmentFeeOverride` value means the user has staged a
    // create-or-replace edit. Coordinates with no entry render the
    // catalogue's current override (or "—" when none exists).
    stagedOverrideByCreditRating?: Record<string, EstablishmentFeeOverride | null>;
  }

  const {
    catalogue,
    channel,
    onCellClick,
    stagedByCoordinate = {},
    onOverrideClick,
    stagedOverrideByCreditRating = {}
  }: Props = $props();

  const pricingModel = $derived(catalogue._dimension_refs?.pricing_model);
  const cube = $derived(catalogue.rate_cube);

  // Bureau-band view
  const bureauBands = $derived(pricingModel === 'bureau_band_x_housing_x_channel' ? Object.keys(cube) : []);
  // Excludes credit-rating-level metadata keys
  // (establishment_fee_override) which sit alongside housing nodes at
  // the cube_outer_level — see pages/src/lib/cube.ts housingIds. Without
  // the filter the editor renders the metadata key as a phantom housing
  // column header with a "—" data row underneath.
  const housingIds = $derived(
    pricingModel === 'bureau_band_x_housing_x_channel' && bureauBands[0]
      ? bureauHousingIds(cube, bureauBands[0])
      : []
  );

  // Grade view
  const gradeRows = $derived(pricingModel === 'grade_x_subgrade_x_channel' ? Object.keys(cube) : []);

  const bandMap = $derived(
    (catalogue as unknown as { _bureau_band_to_public_band_mapping?: Record<string, string[]> })
      ._bureau_band_to_public_band_mapping || {}
  );
  const gradeMap = $derived(
    (catalogue as unknown as { _grade_to_public_band_mapping?: Record<string, string[]> })
      ._grade_to_public_band_mapping || {}
  );

  function publicBandFor(id: string): string {
    if (pricingModel === 'bureau_band_x_housing_x_channel') {
      for (const [pb, ids] of Object.entries(bandMap)) {
        if ((ids as string[]).includes(id)) return pb;
      }
    } else {
      for (const [pb, ids] of Object.entries(gradeMap)) {
        if ((ids as string[]).includes(id)) return pb;
      }
    }
    return '';
  }

  function bandLabel(s: string): string {
    if (!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  function bureauCell(bandId: string, housingId: string): CubeCellValue | undefined {
    return cube[bandId]?.[housingId]?.[channel];
  }
  function gradeCell(gradeSubgrade: string): CubeCellValue | undefined {
    const node = cube[gradeSubgrade] as unknown as Record<string, CubeCellValue> | undefined;
    return node?.[channel];
  }

  function bureauCoord(bandId: string, housingId: string): string {
    return bandId + ' / ' + housingId + ' / ' + channel;
  }
  function gradeCoord(gradeSubgrade: string): string {
    return gradeSubgrade + ' / ' + channel;
  }

  function handleClick(coordinates: string, before: CubeCellValue | undefined): void {
    if (!onCellClick || !before) return;
    onCellClick(coordinates, before);
  }

  function stagedRate(coordinates: string): number | null | undefined {
    return stagedByCoordinate[coordinates]?.interest_rate_pa;
  }
  function stagedAmount(coordinates: string): number | null | undefined {
    return stagedByCoordinate[coordinates]?.max_loan_amount;
  }

  function catalogueOverrideFor(creditRatingId: string): EstablishmentFeeOverride | null {
    const node = (cube as unknown as Record<string, { establishment_fee_override?: EstablishmentFeeOverride }>)[creditRatingId];
    return node?.establishment_fee_override ?? null;
  }

  // The B-lite UX renders a single-tier override as a scalar dollar
  // figure; a multi-tier override renders as "varies" with the popover
  // exposed by the editor. A null / undefined override renders as a
  // muted em-dash.
  function formatOverride(override: EstablishmentFeeOverride | null | undefined): string {
    if (!override || !Array.isArray(override.tiers) || override.tiers.length === 0) return '—';
    if (override.tiers.length === 1) {
      const fee = override.tiers[0]?.fee;
      return typeof fee === 'number' ? formatAmount(fee) : '—';
    }
    return 'varies';
  }

  // The override edit may have staged an override (object), staged a
  // removal (null), or stay untouched (undefined). Distinguish the
  // staged states so the UI surfaces "before → after" correctly.
  function effectiveOverrideFor(creditRatingId: string): {
    catalogueOverride: EstablishmentFeeOverride | null;
    stagedOverride: EstablishmentFeeOverride | null | undefined;
    isStaged: boolean;
  } {
    const catalogueOverride = catalogueOverrideFor(creditRatingId);
    const hasStaged = Object.prototype.hasOwnProperty.call(stagedOverrideByCreditRating, creditRatingId);
    return {
      catalogueOverride,
      stagedOverride: hasStaged ? stagedOverrideByCreditRating[creditRatingId] ?? null : undefined,
      isStaged: hasStaged
    };
  }

  function handleOverrideClick(creditRatingId: string): void {
    if (!onOverrideClick) return;
    onOverrideClick(creditRatingId, catalogueOverrideFor(creditRatingId));
  }
</script>

{#if pricingModel === 'bureau_band_x_housing_x_channel'}
<table class="cube-table">
  <thead>
    <tr>
      <th>Bureau band</th>
      {#each housingIds as housing (housing)}
        <th class="num">% — {housing}{#if onCellClick}<span class="edit-pencil" aria-label="editable column"> ✎</span>{/if}</th>
      {/each}
      <th>Public band</th>
    </tr>
  </thead>
  <tbody>
    {#each bureauBands as bandId (bandId)}
      {@const override = effectiveOverrideFor(bandId)}
      {@const displayOverride = override.isStaged ? override.stagedOverride : override.catalogueOverride}
      <tr>
        <td>
          <code>{bandId}</code>
          {#if onOverrideClick}
            <button
              type="button"
              class="override-row override-editable"
              class:override-staged={override.isStaged}
              onclick={() => handleOverrideClick(bandId)}
            >
              <span class="override-label muted">est.fee</span>
              {#if override.isStaged}
                <span class="muted">{formatOverride(override.catalogueOverride)} →</span>
                <span>{formatOverride(override.stagedOverride)}</span>
              {:else}
                <span>{formatOverride(displayOverride)}</span>
              {/if}
              <span class="edit-pencil" aria-hidden="true"> ✎</span>
            </button>
          {:else}
            <div class="override-row" class:override-staged={override.isStaged}>
              <span class="override-label muted">est.fee</span>
              {#if override.isStaged}
                <span class="muted">{formatOverride(override.catalogueOverride)} →</span>
                <span>{formatOverride(override.stagedOverride)}</span>
              {:else}
                <span>{formatOverride(displayOverride)}</span>
              {/if}
            </div>
          {/if}
        </td>
        {#each housingIds as housing (housing)}
          {@const coord = bureauCoord(bandId, housing)}
          {@const cell = bureauCell(bandId, housing)}
          {@const staged = stagedRate(coord)}
          <td
            class="num"
            class:editable={!!onCellClick}
            class:is-staged={staged !== undefined}
            onclick={() => handleClick(coord, cell)}
          >
            {#if staged !== undefined}
              <span class="muted">{formatRate(cell?.interest_rate_pa)} →</span> {formatRate(staged)}
            {:else}
              {formatRate(cell?.interest_rate_pa)}
            {/if}
          </td>
        {/each}
        <td class="muted">→ {bandLabel(publicBandFor(bandId))}</td>
      </tr>
    {/each}
  </tbody>
</table>
{:else if pricingModel === 'grade_x_subgrade_x_channel'}
<table class="cube-table">
  <thead>
    <tr>
      <th>Grade-subgrade</th>
      <th class="num">% Rate{#if onCellClick}<span class="edit-pencil" aria-label="editable column"> ✎</span>{/if}</th>
      <th class="num">$ Max amount{#if onCellClick}<span class="edit-pencil" aria-label="editable column"> ✎</span>{/if}</th>
      <th>Public band</th>
    </tr>
  </thead>
  <tbody>
    {#each gradeRows as cellId (cellId)}
      {@const coord = gradeCoord(cellId)}
      {@const cell = gradeCell(cellId)}
      {@const sRate = stagedRate(coord)}
      {@const sAmount = stagedAmount(coord)}
      {@const override = effectiveOverrideFor(cellId)}
      {@const displayOverride = override.isStaged ? override.stagedOverride : override.catalogueOverride}
      <tr>
        <td>
          <code>{cellId}</code>
          {#if onOverrideClick}
            <button
              type="button"
              class="override-row override-editable"
              class:override-staged={override.isStaged}
              onclick={() => handleOverrideClick(cellId)}
            >
              <span class="override-label muted">est.fee</span>
              {#if override.isStaged}
                <span class="muted">{formatOverride(override.catalogueOverride)} →</span>
                <span>{formatOverride(override.stagedOverride)}</span>
              {:else}
                <span>{formatOverride(displayOverride)}</span>
              {/if}
              <span class="edit-pencil" aria-hidden="true"> ✎</span>
            </button>
          {:else}
            <div class="override-row" class:override-staged={override.isStaged}>
              <span class="override-label muted">est.fee</span>
              {#if override.isStaged}
                <span class="muted">{formatOverride(override.catalogueOverride)} →</span>
                <span>{formatOverride(override.stagedOverride)}</span>
              {:else}
                <span>{formatOverride(displayOverride)}</span>
              {/if}
            </div>
          {/if}
        </td>
        <td
          class="num"
          class:editable={!!onCellClick}
          class:is-staged={sRate !== undefined}
          onclick={() => handleClick(coord, cell)}
        >
          {#if sRate !== undefined}
            <span class="muted">{formatRate(cell?.interest_rate_pa)} →</span> {formatRate(sRate)}
          {:else}
            {formatRate(cell?.interest_rate_pa)}
          {/if}
        </td>
        <td
          class="num"
          class:editable={!!onCellClick}
          class:is-staged={sAmount !== undefined}
          onclick={() => handleClick(coord, cell)}
        >
          {#if sAmount !== undefined}
            <span class="muted">{formatAmount(cell?.max_loan_amount)} →</span> {formatAmount(sAmount)}
          {:else}
            {formatAmount(cell?.max_loan_amount)}
          {/if}
        </td>
        <td class="muted">→ {bandLabel(publicBandFor(cellId))}</td>
      </tr>
    {/each}
  </tbody>
</table>
{:else}
<p class="muted">Pricing model "{pricingModel}" is not yet supported in the cube view.</p>
{/if}

<style>
  /* Editable cells — brand input-affordance per harmoney-brand.yaml
   * (Blue 100 / Blue 25 are the "field highlight" swatches).
   * Cells signal editability at rest via a subtle blue underline on the
   * value; hover and focus deepen the cue so the click target is
   * unambiguous without requiring discovery. The pencil glyph in the
   * column header mirrors INTERFACE-SPECIFICATION.md §6.2's wireframe. */
  .editable {
    cursor: pointer;
    box-shadow: inset 0 -2px 0 0 var(--input-interactive);
    transition: background-color 80ms ease, box-shadow 80ms ease, color 80ms ease;
  }
  .editable:hover {
    background: var(--input-interactive-bg);
    color: var(--input-interactive);
    box-shadow: inset 0 -2px 0 0 var(--input-interactive);
  }
  .editable:focus,
  .editable:focus-visible {
    outline: 2px solid var(--input-interactive);
    outline-offset: -2px;
    background: var(--input-interactive-bg);
    color: var(--input-interactive);
  }
  .is-staged {
    background: var(--staged-bg);
    color: var(--staged);
    font-weight: 500;
    box-shadow: inset 0 -2px 0 0 var(--staged);
  }
  .is-staged:hover {
    background: var(--staged-bg);
    color: var(--staged);
  }
  .edit-pencil {
    color: var(--input-interactive);
    font-weight: 500;
    margin-left: 0.25em;
  }
  /* Per-credit-rating establishment-fee override sub-line. Sits under
   * the band / grade-subgrade code as a small, indented affordance.
   * The B-lite UX renders a single-tier override as a scalar dollar
   * figure and a multi-tier override as "varies"; clicking opens the
   * override editor in CubeEditor. */
  .override-row {
    margin-top: 0.2em;
    padding: 0.1em 0.25em;
    font-size: 0.82em;
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    line-height: 1.4;
    border-radius: var(--radius-sm);
    color: var(--surface-muted);
    display: inline-flex;
    align-items: baseline;
    gap: 0.35em;
    background: transparent;
    border: none;
  }
  button.override-row {
    cursor: pointer;
    /* Inherit table-cell text alignment; reset button defaults that
     * would otherwise centre the label and add a vertical rhythm gap. */
    text-align: left;
  }
  .override-label {
    text-transform: lowercase;
  }
  .override-editable {
    cursor: pointer;
    box-shadow: inset 0 -2px 0 0 var(--input-interactive);
    transition: background-color 80ms ease, box-shadow 80ms ease, color 80ms ease;
  }
  .override-editable:hover {
    background: var(--input-interactive-bg);
    color: var(--input-interactive);
  }
  .override-editable:focus,
  .override-editable:focus-visible {
    outline: 2px solid var(--input-interactive);
    outline-offset: -2px;
    background: var(--input-interactive-bg);
    color: var(--input-interactive);
  }
  .override-staged {
    background: var(--staged-bg);
    color: var(--staged);
    font-weight: 500;
    box-shadow: inset 0 -2px 0 0 var(--staged);
  }
  code {
    font-family: var(--font-mono);
    font-size: 0.9em;
  }
</style>
